'use client';

import {
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useState,
  useMemo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { css, Z_INDEX_LIBRARY } from '@bts-web/utils-style-engine';
import { Portal } from '@radix-ui/react-portal';

// Drawer types
export type DrawerPosition = 'right' | 'bottom';

export type BaseDrawerProps = {
  disableOverlayClose?: boolean;
  disableCloseButton?: boolean;
  title?: string | null;
  ariaLabel?: string;
  position?: DrawerPosition;
  style?: React.CSSProperties;
};

export type DrawerComponent<T = object> = (
  props: BaseDrawerProps & T,
) => ReactElement;

// Modal types
export type BaseModalProps = {
  disableOverlayClose?: boolean;
  onClose?: () => void;
};

export type ModalComponent<T = object> = (
  props: BaseModalProps & T & { onClose: () => void },
) => ReactElement | null;

// Combined types
type ItemType = 'modal' | 'drawer';

interface BaseItem {
  id: string;
  type: ItemType;
  triggerElement: HTMLElement | null;
}

interface DrawerItem<T extends object = object> extends BaseItem {
  type: 'drawer';
  Component: DrawerComponent<T>;
  props: BaseDrawerProps & T;
}

interface ModalItem<T extends object = object> extends BaseItem {
  type: 'modal';
  Component: ModalComponent<T>;
  props: BaseModalProps & T;
}

type Item<T extends object = object> = DrawerItem<T> | ModalItem<T>;

// Function types
export type OpenDrawerFunction = <T extends object = object>(
  DrawerComponent: DrawerComponent<T>,
  additionalProps?: BaseDrawerProps & T,
) => string;

export type ShowModalFunction = <T extends object = object>(
  ModalComponent: ModalComponent<T>,
  additionalProps?: BaseModalProps & T,
) => string;

export type CloseFunction = (id?: string) => void;

interface ModalDrawerContextProps {
  openDrawer: OpenDrawerFunction;
  showModal: ShowModalFunction;
  close: CloseFunction;
  closeAll: () => void;
}

const ModalDrawerContext = createContext<ModalDrawerContextProps | null>(null);

export function useModalDrawerContext(): ModalDrawerContextProps {
  const context = useContext(ModalDrawerContext);

  if (!context) {
    throw new Error(
      'useModalDrawerContext must be used within a ModalDrawerContextProvider',
    );
  }

  return context;
}

// Legacy hooks for backward compatibility
export function useDrawerContext(): Pick<
  ModalDrawerContextProps,
  'openDrawer' | 'close' | 'closeAll'
> & { closeDrawer: CloseFunction; closeAllDrawers: () => void } {
  const context = useModalDrawerContext();

  return {
    openDrawer: context.openDrawer,
    closeDrawer: context.close,
    closeAllDrawers: context.closeAll,
    close: context.close,
    closeAll: context.closeAll,
  };
}

export function useModalContext(): Pick<
  ModalDrawerContextProps,
  'showModal' | 'close'
> & { onClose: () => void } {
  const context = useModalDrawerContext();

  return {
    showModal: context.showModal,
    onClose: context.close,
    close: context.close,
  };
}

interface DrawerMetadataContextProps {
  position: DrawerPosition;
  closeThisDrawer: () => void;
  drawerId: string;
  disableCloseButton?: boolean;
  title?: string | null;
}

const DrawerMetadataContext = createContext<DrawerMetadataContextProps>({
  position: 'right',
  disableCloseButton: false,
  closeThisDrawer: () => {
    return;
  },
  drawerId: '',
  title: null,
});

export function useDrawerMetadata(): DrawerMetadataContextProps {
  return useContext(DrawerMetadataContext);
}

// Generate unique IDs using timestamp and random values
const generateUniqueId = (type: ItemType): string => {
  return `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const backdropAnimations = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const ModalDrawerContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [openItems, setOpenItems] = useState<Item[]>([]);

  function close(id?: string) {
    const itemToClose = id
      ? openItems.find((item) => item.id === id)
      : openItems[openItems.length - 1];

    if (itemToClose?.triggerElement?.isConnected) {
      itemToClose.triggerElement.focus();
    }

    if (id) {
      setOpenItems((current) => current.filter((item) => item.id !== id));
    } else {
      setOpenItems((current) => current.slice(0, -1));
    }
  }

  function closeAll() {
    setOpenItems([]);
  }

  function openDrawer<T extends object = object>(
    DrawerComponent: DrawerComponent<T>,
    additionalProps?: BaseDrawerProps & T,
  ): string {
    const triggerElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Move focus off the trigger so a repeated Enter/Space keystroke can't
    // re-activate it while it's still mounted behind the drawer.
    triggerElement?.blur();

    const id = generateUniqueId('drawer');

    setOpenItems((current) => [
      ...current,
      {
        id,
        type: 'drawer',
        Component: DrawerComponent as unknown as DrawerComponent<object>,
        props: (additionalProps || {}) as unknown as BaseDrawerProps & object,
        triggerElement,
      } as DrawerItem,
    ]);

    return id;
  }

  function showModal<T extends object = object>(
    ModalComponent: ModalComponent<T>,
    additionalProps?: BaseModalProps & T,
  ): string {
    const triggerElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    triggerElement?.blur();

    const id = generateUniqueId('modal');

    const closeThisModal = () => close(id);

    setOpenItems((current) => [
      ...current,
      {
        id,
        type: 'modal',
        Component: ModalComponent as unknown as ModalComponent<object>,
        props: {
          ...additionalProps,
          onClose: closeThisModal,
        } as unknown as BaseModalProps & object,
        triggerElement,
      } as ModalItem,
    ]);

    return id;
  }

  const contextValue = useMemo(
    () => ({
      openDrawer,
      showModal,
      close,
      closeAll,
    }),
    [openItems],
  );

  return (
    <ModalDrawerContext.Provider value={contextValue}>
      {children}

      <Portal>
        <AnimatePresence>
          {/* Render Items with their own backdrops */}
          {openItems.map((item, index) => {
            const baseZIndex =
              typeof Z_INDEX_LIBRARY.DRAWER === 'number'
                ? Z_INDEX_LIBRARY.DRAWER
                : 1000;

            const zIndex = baseZIndex + index * 2;

            const { id, type, Component } = item;

            const disableOverlayClose = item.props.disableOverlayClose;

            const closeThisItem = () => close(id);

            function handleOverlayClick(e: React.MouseEvent) {
              // Only close if clicking on the backdrop itself, not on child elements
              if (e.target === e.currentTarget && !disableOverlayClose) {
                close(id);
              }
            }

            if (type === 'drawer') {
              const drawerItem = item as DrawerItem;

              const { props } = drawerItem;

              const position = props.position || 'right';

              return (
                <motion.div
                  key={`backdrop-${id}`}
                  className={css({
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(166, 166, 166, 0.50)',
                    display: 'flex',
                    alignItems: position === 'bottom' ? 'flex-end' : 'center',
                    justifyContent:
                      position === 'right' ? 'flex-end' : 'center',
                  })}
                  style={{
                    zIndex,
                    backdropFilter: 'blur(3px)',
                    ...props.style,
                  }}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={backdropAnimations}
                  onClick={handleOverlayClick}
                  data-element={`backdrop-${id}`}
                >
                  <DrawerMetadataContext.Provider
                    value={{
                      position,
                      closeThisDrawer: closeThisItem,
                      drawerId: id,
                      disableCloseButton: props.disableCloseButton,
                      title: props.title,
                    }}
                  >
                    <Component {...props} />
                  </DrawerMetadataContext.Provider>
                </motion.div>
              );
            } else {
              const modalItem = item as ModalItem;

              const { props } = modalItem;

              return (
                <motion.div
                  key={`backdrop-${id}`}
                  className={css({
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(166, 166, 166, 0.50)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  })}
                  style={{ zIndex, backdropFilter: 'blur(3px)' }}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={backdropAnimations}
                  onClick={handleOverlayClick}
                  data-element={`backdrop-${id}`}
                >
                  <Component {...(props as any)} onClose={closeThisItem} />
                </motion.div>
              );
            }
          })}
        </AnimatePresence>
      </Portal>
    </ModalDrawerContext.Provider>
  );
};

// Legacy provider exports for backward compatibility
export const DrawerContextProvider = ModalDrawerContextProvider;

export const ModalContextProvider = ModalDrawerContextProvider;
